import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MaterialReactTable } from "material-react-table";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import GroupsIcon from "@mui/icons-material/Groups";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";

import { axiosInstance } from "../../../../../api/axios";
import { useProjectApi } from "../../../hooks/useProjectApi";
import { MRT_Localization_KO } from "../../../constants/localiztion";
import { FONT_PROPS } from "../../../styles/colors";
import { AutoPilotGuide } from "../../../components/common/AutoPilotGuide";
import { EmptyState } from "../../../components/common/EmptyState";
import { ImageUploadBox } from "../../../components/common/ImageUploadBox";

const STATUS_OPTIONS = [
  { value: "completed", label: "완료", color: "success", icon: <CheckCircleIcon /> },
  { value: "missed", label: "미실시", color: "error", icon: <EventBusyIcon /> },
  { value: "unregistered", label: "미등록", color: "default", icon: <CalendarMonthIcon /> },
];

const TBM_AUTOPILOT_STEPS = [
  {
    target: '[data-autopilot="tbm-header"]',
    title: "TBM 일일 관리",
    description:
      "조회 일자를 기준으로 팀/라인별 TBM 실시 상태를 확인합니다. 안내 과정에서는 실제 TBM 데이터가 저장되거나 변경되지 않습니다.",
  },
  {
    target: '[data-autopilot="tbm-actions"]',
    title: "조회와 등록",
    description:
      "날짜를 바꾸면 해당 일자의 TBM 목록을 조회합니다. 등록 버튼에서는 진행자, 참석자, 체크리스트, 현장 사진을 입력합니다.",
  },
  {
    target: '[data-autopilot="tbm-summary"]',
    title: "실시 현황 요약",
    description:
      "완료, 미실시, 미등록, 전체 대상 인원과 참석률을 요약합니다. 누락된 라인과 참석률이 낮은 팀을 빠르게 파악할 수 있습니다.",
  },
  {
    target: '[data-autopilot="tbm-list"]',
    title: "TBM 목록",
    description:
      "웹에서는 테이블로, 모바일에서는 카드로 TBM 기록을 확인합니다. 참석률과 상태를 우선 보여 현장 대응이 쉽도록 구성했습니다.",
  },
  {
    target: '[data-autopilot="tbm-table-actions"]',
    title: "검색",
    description:
      "팀/라인이나 진행자로 검색하고 선택 항목을 삭제할 수 있습니다.",
  },
];

const getToday = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const formatDate = (date) => {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const getMonthBounds = (dateText = getToday()) => {
  const [year, month] = String(dateText).split("-").map(Number);
  const base = Number.isFinite(year) && Number.isFinite(month) ? new Date(year, month - 1, 1) : new Date();
  const start = new Date(base.getFullYear(), base.getMonth(), 1);
  const end = new Date(base.getFullYear(), base.getMonth() + 1, 0);
  return {
    start: formatDate(start),
    end: formatDate(end),
    label: `${start.getFullYear()}년 ${start.getMonth() + 1}월`,
  };
};

const getShiftedMonthDate = (dateText = getToday(), offset = 0) => {
  const [year, month] = String(dateText).split("-").map(Number);
  const base = Number.isFinite(year) && Number.isFinite(month) ? new Date(year, month - 1, 1) : new Date();
  return formatDate(new Date(base.getFullYear(), base.getMonth() + offset, 1));
};

const getDefaultDateTime = (date = getToday()) => `${date}T08:00`;

const getStatusMeta = (status) =>
  STATUS_OPTIONS.find((item) => item.value === status) || STATUS_OPTIONS[2];

const resolvePhotoUrl = (photoUrl) => {
  if (!photoUrl) return "";
  if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
  return `${axiosInstance.defaults.baseURL || ""}${photoUrl}`;
};

const makeEmptyForm = (date, options = {}) => ({
  location_id: "",
  department_id: "",
  team_name: "",
  leader_emp_id: "",
  session_date: getDefaultDateTime(date),
  status: "completed",
  total_members: 0,
  attendees: [],
  checklist: (options.checklistTemplates || []).map((item) => ({
    template_id: Number(item.id),
    is_checked: false,
  })),
});

const buildTeamCreateForm = (date, options = {}, team = {}) => {
  const base = makeEmptyForm(date, options);
  const department = (options.departments || []).find(
    (item) => String(item.department_id) === String(team.department_id)
  );
  const departmentId = department?.department_id || team.department_id || "";
  const teamName = department?.department_name || team.team_name || "";
  const teamEmployees = departmentId
    ? (options.employees || []).filter(
        (employee) => String(employee.department_id || "") === String(departmentId)
      )
    : [];

  return {
    ...base,
    department_id: departmentId ? String(departmentId) : "",
    team_name: teamName,
    session_date: team.session_date_input || getDefaultDateTime(date),
    status: "completed",
    total_members: teamEmployees.length || team.total_members || 0,
    attendees: teamEmployees.map((employee) => ({
      emp_id: Number(employee.emp_id),
      is_attended: true,
    })),
  };
};

const normalizeBool = (value) => value === true || value === 1 || value === "1";

const buildEditForm = (session, options = {}, date = getToday()) => {
  const checklistMap = new Map(
    (session.checklist || []).map((item) => [Number(item.template_id), normalizeBool(item.is_checked)])
  );
  const matchedDepartment = (options.departments || []).find(
    (department) => String(department.department_name || "") === String(session.team_name || "")
  );

  return {
    location_id: session.location_id || "",
    department_id: matchedDepartment?.department_id ? String(matchedDepartment.department_id) : "",
    team_name: session.team_name || "",
    leader_emp_id: session.leader_emp_id ? String(session.leader_emp_id) : "",
    session_date: session.session_date_input || getDefaultDateTime(date),
    status: session.status || "unregistered",
    total_members: session.total_members || 0,
    attendees: (session.attendees || []).map((item) => ({
      emp_id: Number(item.emp_id),
      is_attended: normalizeBool(item.is_attended),
    })),
    checklist: (options.checklistTemplates || []).map((item) => ({
      template_id: Number(item.id),
      is_checked: checklistMap.get(Number(item.id)) || false,
    })),
  };
};

const buildFormData = (form, photoFile) => {
  const attendees = form.attendees || [];
  const formData = new FormData();

  formData.append("location_id", form.location_id || "");
  formData.append("team_name", form.team_name || "");
  formData.append("leader_emp_id", form.leader_emp_id || "");
  formData.append("session_date", form.session_date || "");
  formData.append("status", form.status || "unregistered");
  formData.append("total_members", Number(form.total_members || attendees.length || 0));
  formData.append(
    "attendees",
    JSON.stringify(
      attendees.map((item) => ({
        emp_id: Number(item.emp_id),
        is_attended: Boolean(item.is_attended),
      }))
    )
  );
  formData.append(
    "checklist",
    JSON.stringify(
      (form.checklist || []).map((item) => ({
        template_id: Number(item.template_id),
        is_checked: Boolean(item.is_checked),
      }))
    )
  );

  if (photoFile) {
    formData.append("photo", photoFile);
  }

  return formData;
};

const TILE_COLORS = {
  primary: { main: "#1976d2", bg: "#e8f2ff" },
  success: { main: "#2e7d32", bg: "#e8f5e9" },
  warning: { main: "#ed6c02", bg: "#fff3e0" },
  error: { main: "#d32f2f", bg: "#ffebee" },
};

const MONTH_STATUS_META = {
  completed: { label: "완료", color: "#16a34a", bg: "#ecfdf5", border: "#bbf7d0" },
  missed: { label: "미실시", color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  unregistered: { label: "미등록", color: "#d97706", bg: "#fff7ed", border: "#fed7aa" },
};

const getMonthTbmStatusSummary = (day = {}) => {
  const completed = Number(day.completed || 0);
  const missed = Number(day.missed || 0);
  const unregistered = Number(day.unregistered || 0);
  const total = Number(day.total || completed + missed + unregistered);
  const meta = MONTH_STATUS_META[day.status] || MONTH_STATUS_META.unregistered;

  if (total > 0 && completed === total && missed === 0 && unregistered === 0) {
    return {
      ...MONTH_STATUS_META.completed,
      isAllComplete: true,
      label: "전체완료",
      title: `완료 ${completed}팀`,
      items: [],
    };
  }

  return {
    ...meta,
    isAllComplete: false,
    label: "상태 집계",
    title: `완료 ${completed}팀 / 미실시 ${missed}팀 / 미등록 ${unregistered}팀`,
    items: [
      { key: "completed", label: "완료", count: completed, ...MONTH_STATUS_META.completed },
      { key: "missed", label: "미실시", count: missed, ...MONTH_STATUS_META.missed },
      { key: "unregistered", label: "미등록", count: unregistered, ...MONTH_STATUS_META.unregistered },
    ],
  };
};

function SummaryTile({ icon, label, value, color = "primary", helper }) {
  const palette = TILE_COLORS[color] || TILE_COLORS.primary;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        height: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        backgroundColor: "#fff",
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar
          sx={{
            width: 40,
            height: 40,
            color: palette.main,
            bgcolor: palette.bg,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" color="text.secondary">
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function TbmFormDialog({
  open,
  mode,
  form,
  employees,
  departments,
  checklistTemplates,
  photoFile,
  isMobile,
  isSaving,
  isLoadingDetail,
  onClose,
  onChange,
  onPhotoChange,
  onSubmit,
}) {
  const [employeeFilter, setEmployeeFilter] = useState("");
  const isEdit = mode === "edit";
  const dialogTitle = isEdit
    ? isMobile
      ? "TBM 기록 수정"
      : "TBM 실시 내역 수정"
    : isMobile
      ? "현장 TBM 기록"
      : "TBM 실시 내역 등록";
  const submitLabel = isEdit ? "수정 완료" : isMobile ? "기록 완료" : "등록 완료";

  const attendeeMap = useMemo(
    () => new Map((form.attendees || []).map((item) => [Number(item.emp_id), item])),
    [form.attendees]
  );

  const checklistMap = useMemo(
    () => new Map((form.checklist || []).map((item) => [Number(item.template_id), item])),
    [form.checklist]
  );

  const teamEmployees = useMemo(() => {
    if (!form.department_id) return employees;
    return employees.filter(
      (employee) => String(employee.department_id || "") === String(form.department_id)
    );
  }, [employees, form.department_id]);

  const filteredEmployees = useMemo(() => {
    const keyword = employeeFilter.trim().toLowerCase();
    if (!keyword) return teamEmployees;
    return teamEmployees.filter((employee) => {
      const text = [
        employee.emp_name,
        employee.department_name,
        employee.position_name,
        employee.emp_id,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(keyword);
    });
  }, [employeeFilter, teamEmployees]);

  const handleDepartmentChange = (value) => {
    const selectedDepartment = (departments || []).find(
      (department) => String(department.department_id) === String(value)
    );
    const nextTeamName = selectedDepartment?.department_name || "";
    const nextEmployees = value
      ? employees.filter((employee) => String(employee.department_id || "") === String(value))
      : [];
    const nextAttendees = nextEmployees.map((employee) => ({
      emp_id: Number(employee.emp_id),
      is_attended: true,
    }));
    const leaderStillInTeam = nextEmployees.some(
      (employee) => String(employee.emp_id) === String(form.leader_emp_id || "")
    );

    onChange("department_id", value);
    onChange("team_name", nextTeamName);
    onChange("attendees", nextAttendees);
    onChange("total_members", nextAttendees.length);
    if (!leaderStillInTeam) onChange("leader_emp_id", "");
    setEmployeeFilter("");
  };

  const targetCount = form.attendees?.length || 0;
  const attendedCount = (form.attendees || []).filter((item) => item.is_attended).length;

  const setAttendeeTarget = (employee, checked) => {
    const empId = Number(employee.emp_id);
    if (checked) {
      onChange("attendees", [
        ...(form.attendees || []),
        {
          emp_id: empId,
          is_attended: true,
        },
      ]);
      if (!form.total_members || Number(form.total_members) < targetCount + 1) {
        onChange("total_members", targetCount + 1);
      }
      return;
    }

    const nextAttendees = (form.attendees || []).filter((item) => Number(item.emp_id) !== empId);
    onChange("attendees", nextAttendees);
    if (Number(form.total_members) === targetCount) {
      onChange("total_members", nextAttendees.length);
    }
  };

  const setAttendeeStatus = (employee, checked) => {
    const empId = Number(employee.emp_id);
    onChange(
      "attendees",
      (form.attendees || []).map((item) =>
        Number(item.emp_id) === empId ? { ...item, is_attended: checked } : item
      )
    );
  };

  const setChecklistChecked = (template, checked) => {
    const templateId = Number(template.id);
    const existing = checklistMap.get(templateId);
    if (existing) {
      onChange(
        "checklist",
        (form.checklist || []).map((item) =>
          Number(item.template_id) === templateId ? { ...item, is_checked: checked } : item
        )
      );
      return;
    }
    onChange("checklist", [
      ...(form.checklist || []),
      { template_id: templateId, is_checked: checked },
    ]);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
      <DialogTitle
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: "calc(env(safe-area-inset-top, 0px) + 18px)", sm: 2 },
          pb: { xs: 1.5, sm: 1 },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {dialogTitle}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip size="small" icon={<GroupsIcon />} label={`대상 ${targetCount}명`} />
              <Chip size="small" color="success" label={`참석 ${attendedCount}명`} />
            </Stack>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          backgroundColor: "#f7f8fb",
          px: { xs: 2, sm: 3 },
          py: { xs: 2.25, sm: 2 },
        }}
      >
        {isLoadingDetail ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Grid container spacing={2.25}>
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ fontWeight: 800, mb: 2 }}>기본 정보</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>팀/라인</InputLabel>
                      <Select
                        label="팀/라인"
                        value={form.department_id || ""}
                        onChange={(event) => handleDepartmentChange(event.target.value)}
                      >
                        <MenuItem value="" disabled>
                          팀/부서 선택
                        </MenuItem>
                        {!(departments || []).length ? (
                          <MenuItem value="" disabled>
                            등록된 팀/부서가 없습니다
                          </MenuItem>
                        ) : null}
                        {(departments || []).map((department) => (
                          <MenuItem
                            key={department.department_id}
                            value={String(department.department_id)}
                          >
                            {department.department_name}
                            {department.member_count ? ` / ${department.member_count}명` : ""}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>진행자</InputLabel>
                      <Select
                        label="진행자"
                        value={form.leader_emp_id || ""}
                        onChange={(event) => onChange("leader_emp_id", event.target.value)}
                      >
                        {teamEmployees.map((employee) => (
                          <MenuItem key={employee.emp_id} value={String(employee.emp_id)}>
                            {employee.emp_name || employee.emp_id}
                            {employee.department_name ? ` / ${employee.department_name}` : ""}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      required
                      type="datetime-local"
                      label="실시 일시"
                      value={form.session_date}
                      onChange={(event) => onChange("session_date", event.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>상태</InputLabel>
                      <Select
                        label="상태"
                        value={form.status}
                        onChange={(event) => onChange("status", event.target.value)}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="number"
                      label="전체 대상 인원"
                      inputProps={{ min: 0 }}
                      value={form.total_members}
                      onChange={(event) => onChange("total_members", event.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ImageUploadBox
                      label="현장 사진"
                      file={photoFile}
                      onFileChange={onPhotoChange}
                      compact
                    />
                  </Grid>
                </Grid>
              </Paper>

              <Paper
                elevation={0}
                sx={{ p: 2, mt: 2, border: "1px solid", borderColor: "divider" }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  sx={{ mb: 1.5 }}
                >
                  <Typography sx={{ fontWeight: 800 }}>참석자</Typography>
                  <TextField
                    size="small"
                    placeholder="직원 검색"
                    value={employeeFilter}
                    onChange={(event) => setEmployeeFilter(event.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Stack>
                <List
                  disablePadding
                  sx={{
                    maxHeight: { xs: 340, md: 440 },
                    overflow: "auto",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fff",
                  }}
                >
                  {filteredEmployees.map((employee) => {
                    const attendee = attendeeMap.get(Number(employee.emp_id));
                    const selected = Boolean(attendee);
                    return (
                      <ListItem
                        key={employee.emp_id}
                        divider
                        secondaryAction={
                          <FormControlLabel
                            control={
                              <Switch
                                edge="end"
                                checked={Boolean(attendee?.is_attended)}
                                disabled={!selected}
                                onChange={(event) =>
                                  setAttendeeStatus(employee, event.target.checked)
                                }
                              />
                            }
                            label={attendee?.is_attended ? "참석" : "불참"}
                            labelPlacement="start"
                          />
                        }
                      >
                        <Checkbox
                          checked={selected}
                          onChange={(event) => setAttendeeTarget(employee, event.target.checked)}
                        />
                        <ListItemText
                          primary={employee.emp_name || employee.emp_id}
                          secondary={[employee.department_name, employee.position_name]
                            .filter(Boolean)
                            .join(" / ")}
                        />
                      </ListItem>
                    );
                  })}
                  {!filteredEmployees.length ? (
                    <ListItem>
                      <ListItemText primary="직원 데이터가 없습니다." />
                    </ListItem>
                  ) : null}
                </List>
              </Paper>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ p: 2, border: "1px solid", borderColor: "divider" }}>
                <Typography sx={{ fontWeight: 800, mb: 1.5 }}>체크리스트</Typography>
                <Stack spacing={1}>
                  {checklistTemplates.map((template) => {
                    const item = checklistMap.get(Number(template.id));
                    return (
                      <Paper
                        key={template.id}
                        variant="outlined"
                        sx={{ px: 1.5, py: 1, bgcolor: "#fff" }}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(item?.is_checked)}
                              onChange={(event) => setChecklistChecked(template, event.target.checked)}
                            />
                          }
                          label={template.title}
                          sx={{ m: 0, width: "100%" }}
                        />
                      </Paper>
                    );
                  })}
                  {!checklistTemplates.length ? (
                    <Alert severity="info">활성 체크리스트 항목이 없습니다.</Alert>
                  ) : null}
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{ p: 2, mt: 2, border: "1px solid", borderColor: "divider" }}
              >
                <Typography sx={{ fontWeight: 800, mb: 1.5 }}>참석률</Typography>
                <LinearProgress
                  variant="determinate"
                  value={targetCount ? Math.round((attendedCount / targetCount) * 100) : 0}
                  sx={{ height: 10, borderRadius: 999 }}
                />
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {attendedCount}/{targetCount}명
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    {targetCount ? Math.round((attendedCount / targetCount) * 100) : 0}%
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2.25, sm: 2 },
          gap: 1,
          justifyContent: { xs: "center", sm: "flex-end" },
          borderTop: { xs: "1px solid", sm: "none" },
          borderColor: "divider",
          bgcolor: "#fff",
        }}
      >
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ flex: { xs: 1, sm: "initial" }, minHeight: { xs: 44, sm: 36 } }}
        >
          취소
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          disabled={isSaving || isLoadingDetail}
          startIcon={isSaving ? null : <PlaylistAddCheckIcon />}
          sx={{ flex: { xs: 1, sm: "initial" }, minHeight: { xs: 44, sm: 36 } }}
        >
          {isSaving ? "저장 중" : submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MobileSessionCard({ session, onEdit, onCreateFromTeam }) {
  const status = getStatusMeta(session.status);
  const attended = Number(session.attended_count || 0);
  const total = Number(session.total_members || 0);
  const rate = total ? Math.round((attended / total) * 100) : 0;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {session.team_name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {session.session_time || "--:--"} / {session.leader_name || "진행자 미등록"}
              </Typography>
            </Box>
            <Chip size="small" color={status.color} label={status.label} />
          </Stack>

          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                참석률
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                {rate}%
              </Typography>
            </Stack>
            <LinearProgress variant="determinate" value={rate} sx={{ height: 8, borderRadius: 999 }} />
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="small" icon={<GroupsIcon />} label={`${attended}/${total}명`} />
            {session.photo_url ? (
              <Chip
                size="small"
                icon={<CameraAltIcon />}
                label="사진"
                component="a"
                href={resolvePhotoUrl(session.photo_url)}
                target="_blank"
                clickable
              />
            ) : null}
          </Stack>
        </Stack>
      </CardContent>
      <Divider />
      <CardActions sx={{ justifyContent: "flex-end" }}>
        {session.virtual ? (
          <Button size="small" startIcon={<AddIcon />} onClick={() => onCreateFromTeam(session)}>
            등록
          </Button>
        ) : (
          <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(session)}>
            수정
          </Button>
        )}
      </CardActions>
    </Card>
  );
}

function MonthlyTbmCalendar({ title, days, selectedDate, isLoading, onSelectDate, onMonthChange, isMobile = false }) {
  const summary = useMemo(() => {
    const rows = days || [];
    return {
      completed: rows.reduce((sum, day) => sum + Number(day.completed || 0), 0),
      missed: rows.reduce((sum, day) => sum + Number(day.missed || 0), 0),
      unregistered: rows.reduce((sum, day) => sum + Number(day.unregistered || 0), 0),
    };
  }, [days]);
  const leadingBlankCount = useMemo(() => {
    const firstDate = days?.[0]?.date;
    if (!firstDate) return 0;
    return new Date(`${firstDate}T00:00:00`).getDay();
  }, [days]);

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2 },
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        bgcolor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" },
          alignItems: "center",
          gap: 1.25,
          mb: 1.75,
        }}
      >
        <Box sx={{ display: { xs: "none", sm: "block" } }} />
        <Stack
          direction="row"
          spacing={0.8}
          alignItems="center"
          justifyContent="center"
          sx={{
            justifySelf: "center",
            px: 0.65,
            py: 0.55,
            border: "1px solid #e5e7eb",
            borderRadius: "999px",
            bgcolor: "#f8fafc",
            boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
          }}
        >
            <Tooltip title="이전 달">
              <IconButton
                size="small"
                onClick={() => onMonthChange?.(-1)}
                sx={{
                  width: 32,
                  height: 32,
                  border: "1px solid",
                  borderColor: "#e5e7eb",
                  borderRadius: "50%",
                  bgcolor: "#fff",
                  color: "#4f46e5",
                  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.06)",
                  "&:hover": {
                    bgcolor: "#eef2ff",
                    borderColor: "#c7d2fe",
                  },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Stack
              direction="row"
              spacing={0.8}
              alignItems="center"
              justifyContent="center"
              sx={{
                minWidth: { xs: 190, sm: 250 },
                px: { xs: 0.4, sm: 1 },
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 18, color: "#635bff" }} />
              <Typography
                sx={{
                  fontWeight: 950,
                  fontSize: { xs: 15, md: 17 },
                  color: "#111827",
                  letterSpacing: 0,
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                }}
              >
                {title} TBM 현황
              </Typography>
            </Stack>
            <Tooltip title="다음 달">
              <IconButton
                size="small"
                onClick={() => onMonthChange?.(1)}
                sx={{
                  width: 32,
                  height: 32,
                  border: "1px solid",
                  borderColor: "#e5e7eb",
                  borderRadius: "50%",
                  bgcolor: "#fff",
                  color: "#4f46e5",
                  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.06)",
                  "&:hover": {
                    bgcolor: "#eef2ff",
                    borderColor: "#c7d2fe",
                  },
                }}
              >
                <ArrowForwardIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
        </Stack>
        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          justifyContent={{ xs: "center", sm: "flex-end" }}
          sx={{ justifySelf: { xs: "center", sm: "end" } }}
        >
          <Chip
            size="small"
            label={`완료 ${summary.completed}팀`}
            variant="outlined"
            sx={{
              height: 28,
              borderRadius: "999px",
              bgcolor: "#f0fdf4",
              borderColor: "#86efac",
              color: "#15803d",
              fontWeight: 900,
              "& .MuiChip-label": { px: 1.1 },
            }}
          />
          <Chip
            size="small"
            label={`미실시 ${summary.missed}팀`}
            variant="outlined"
            sx={{
              height: 28,
              borderRadius: "999px",
              bgcolor: "#fef2f2",
              borderColor: "#fecaca",
              color: "#dc2626",
              fontWeight: 900,
              "& .MuiChip-label": { px: 1.1 },
            }}
          />
        </Stack>
      </Box>

      {isLoading ? (
        <Stack alignItems="center" sx={{ py: 3 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
            gap: 0.75,
          }}
        >
          {["일", "월", "화", "수", "목", "금", "토"].map((label) => (
            <Typography
              key={label}
              variant="caption"
              color="text.secondary"
              textAlign="center"
              sx={{ fontWeight: 900 }}
            >
              {label}
            </Typography>
          ))}
          {Array.from({ length: leadingBlankCount }).map((_, index) => (
            <Box key={`blank-${index}`} />
          ))}
          {(days || []).map((day) => {
            const meta = MONTH_STATUS_META[day.status] || MONTH_STATUS_META.unregistered;
            const isSelected = day.date === selectedDate;
            const statusSummary = getMonthTbmStatusSummary(day);
            return (
              <Box
                key={day.date}
                role="button"
                tabIndex={0}
                onClick={() => onSelectDate(day.date)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") onSelectDate(day.date);
                }}
                sx={{
                  minHeight: { xs: 104, md: 92 },
                  p: { xs: 0.85, md: 1.1 },
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: isSelected ? "primary.main" : meta.border,
                  bgcolor: isSelected ? "#eef2ff" : meta.bg,
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  outline: "none",
                }}
              >
                <Typography sx={{ fontWeight: 900, color: "#111827", lineHeight: 1 }}>
                  {Number(String(day.date).slice(8, 10))}
                </Typography>
                {statusSummary.isAllComplete ? (
                  <Box
                    title={statusSummary.title}
                    sx={{
                      width: "100%",
                      mt: 1,
                      px: { xs: 0.5, md: 1 },
                      py: 1,
                      flex: 1,
                      minHeight: { xs: 48, md: 56 },
                      borderRadius: "14px",
                      bgcolor: statusSummary.bg,
                      border: "1px solid",
                      borderColor: statusSummary.border,
                      color: statusSummary.color,
                      fontSize: { xs: 11.5, md: 14, xl: 14.5 },
                      fontWeight: 950,
                      lineHeight: 1.2,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {statusSummary.label}
                  </Box>
                ) : (
                  <Stack
                    title={statusSummary.title}
                    direction={isMobile ? "column" : "row"}
                    spacing={isMobile ? 0.35 : 0.55}
                    sx={{
                      width: "100%",
                      mt: { xs: 0.65, md: 1 },
                      minWidth: 0,
                    }}
                  >
                    {statusSummary.items.map((item) => (
                      <Box
                        key={item.key}
                        sx={{
                          flex: 1,
                          width: "100%",
                          minWidth: 0,
                          px: { xs: 0.65, md: 0.45 },
                          py: { xs: 0.35, md: 0.7 },
                          minHeight: { xs: 22, md: 36 },
                          borderRadius: "999px",
                          bgcolor: item.count > 0 ? item.bg : "#f8fafc",
                          border: "1px solid",
                          borderColor: item.count > 0 ? item.border : "#e2e8f0",
                          color: item.count > 0 ? item.color : "#94a3b8",
                          fontSize: { xs: 10.75, md: 11.5, xl: 12 },
                          fontWeight: 950,
                          lineHeight: 1.15,
                          textAlign: "center",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 0.25,
                        }}
                      >
                        <Box component="span">{item.label}</Box>
                        <Box component="span">{item.count}</Box>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}

export function TbmManagement() {
  const { group_code, project_code } = useParams();
  const api = useProjectApi(group_code, project_code);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedDate, setSelectedDate] = useState(getToday());
  const [teamFilter, setTeamFilter] = useState("all");
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(makeEmptyForm(getToday()));
  const [photoFile, setPhotoFile] = useState(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [autopilotStepIndex, setAutopilotStepIndex] = useState(0);
  const monthBounds = useMemo(() => getMonthBounds(selectedDate), [selectedDate]);

  const optionsQuery = useQuery({
    queryKey: ["risk-tbm-options", group_code, project_code],
    queryFn: async () => {
      const response = await api.get("/tbm/options");
      return response.data || { employees: [], departments: [], checklistTemplates: [] };
    },
    enabled: Boolean(group_code && project_code),
    placeholderData: { employees: [], departments: [], checklistTemplates: [] },
  });

  const sessionsQuery = useQuery({
    queryKey: ["risk-tbm-sessions", group_code, project_code, selectedDate],
    queryFn: async () => {
      const response = await api.get("/tbm/sessions", { params: { date: selectedDate } });
      return response.data || [];
    },
    enabled: Boolean(group_code && project_code && selectedDate),
    placeholderData: [],
  });

  const monthCalendarQuery = useQuery({
    queryKey: [
      "risk-tbm-calendar",
      group_code,
      project_code,
      monthBounds.start,
      monthBounds.end,
    ],
    queryFn: async () => {
      const response = await api.get("/tbm/calendar", {
        params: {
          start: monthBounds.start,
          end: monthBounds.end,
        },
      });
      return Array.isArray(response.data) ? response.data : response.data?.days || [];
    },
    enabled: Boolean(group_code && project_code && monthBounds.start && monthBounds.end),
    placeholderData: [],
  });

  const employees = useMemo(() => optionsQuery.data?.employees || [], [optionsQuery.data]);
  const departments = useMemo(() => optionsQuery.data?.departments || [], [optionsQuery.data]);
  const checklistTemplates = useMemo(() => optionsQuery.data?.checklistTemplates || [], [optionsQuery.data]);
  const selectedDepartment = useMemo(
    () =>
      departments.find((department) => String(department.department_id) === String(teamFilter)),
    [departments, teamFilter]
  );
  const sessions = useMemo(() => sessionsQuery.data || [], [sessionsQuery.data]);
  const visibleSessions = useMemo(() => {
    if (teamFilter === "all") return sessions;
    return sessions.filter((session) => {
      if (session.department_id) {
        return String(session.department_id) === String(teamFilter);
      }
      return (
        selectedDepartment?.department_name &&
        String(session.team_name || "").trim() === selectedDepartment.department_name
      );
    });
  }, [sessions, selectedDepartment, teamFilter]);

  const invalidateSessions = () => {
    queryClient.invalidateQueries({
      queryKey: ["risk-tbm-sessions", group_code, project_code, selectedDate],
    });
    queryClient.invalidateQueries({
      queryKey: ["risk-tbm-calendar", group_code, project_code],
    });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildFormData(form, photoFile);
      if (dialogMode === "edit") {
        return api.put(`/tbm/sessions/${editingId}`, payload);
      }
      return api.post("/tbm/sessions", payload);
    },
    onSuccess: () => {
      toast.success(dialogMode === "edit" ? "TBM 실시 내역이 저장되었습니다." : "TBM 실시 내역이 등록되었습니다.");
      closeDialog();
      invalidateSessions();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "TBM 저장 중 오류가 발생했습니다.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (ids) => api.delete("/tbm/sessions", { data: { ids } }),
    onSuccess: () => {
      toast.success("선택한 TBM 실시 내역이 삭제되었습니다.");
      setRowSelection({});
      invalidateSessions();
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || "TBM 삭제 중 오류가 발생했습니다.");
    },
  });

  const summary = useMemo(() => {
    const rows = visibleSessions;
    const totalTargets = rows.reduce((sum, row) => sum + Number(row.total_members || 0), 0);
    const totalAttended = rows.reduce((sum, row) => sum + Number(row.attended_count || 0), 0);
    const completed = rows.filter((row) => row.status === "completed").length;
    const missed = rows.filter((row) => row.status === "missed").length;
    const unregistered = rows.filter((row) => row.status === "unregistered").length;
    const rate = totalTargets ? Math.round((totalAttended / totalTargets) * 100) : 0;
    return { total: rows.length, totalTargets, totalAttended, completed, missed, unregistered, rate };
  }, [visibleSessions]);

  const dailyTbmStatus = useMemo(() => {
    if (sessionsQuery.isLoading) return null;
    if (summary.missed > 0) {
      return {
        severity: "error",
        title: "팀별 TBM 미실시",
        message: `${summary.missed}개 팀의 TBM 미실시 상태를 확인해야 합니다.`,
      };
    }
    if (summary.unregistered > 0) {
      return {
        severity: "warning",
        title: "팀별 TBM 미등록",
        message: `${summary.unregistered}개 팀의 TBM 실시 내역이 아직 등록되지 않았습니다.`,
      };
    }
    if (summary.completed > 0) {
      return {
        severity: "success",
        title: "팀별 TBM 완료",
        message: "선택한 날짜의 대상 팀 TBM이 완료되었습니다.",
      };
    }
    return {
      severity: "info",
      title: "팀별 TBM 확인",
      message: "조회할 팀을 선택하거나 TBM 실시 내역을 등록해주세요.",
    };
  }, [sessionsQuery.isLoading, summary.completed, summary.missed, summary.unregistered]);

  const autopilotSteps = useMemo(
    () =>
      isMobile
        ? TBM_AUTOPILOT_STEPS.filter(
            (step) => step.target !== '[data-autopilot="tbm-table-actions"]'
          )
        : TBM_AUTOPILOT_STEPS,
    [isMobile]
  );

  const openAutopilot = () => {
    setAutopilotStepIndex(0);
    setAutopilotOpen(true);
  };

  const openCreateDialog = (team = null) => {
    const targetTeam =
      team ||
      (teamFilter !== "all" && selectedDepartment
        ? {
            department_id: selectedDepartment.department_id,
            team_name: selectedDepartment.department_name,
          }
        : null);
    const nextForm = targetTeam
      ? buildTeamCreateForm(selectedDate, optionsQuery.data, targetTeam)
      : makeEmptyForm(selectedDate, optionsQuery.data);
    setDialogMode("create");
    setEditingId(null);
    setForm(nextForm);
    setPhotoFile(null);
    setDialogOpen(true);
  };

  const openEditDialog = async (session) => {
    setDialogMode("edit");
    setEditingId(session.id);
    setDialogOpen(true);
    setPhotoFile(null);
    setIsLoadingDetail(true);

    try {
      const response = await api.get(`/tbm/sessions/${session.id}`);
      setForm(buildEditForm(response.data || session, optionsQuery.data, selectedDate));
    } catch (error) {
      toast.error(error?.response?.data?.message || "TBM 상세 조회 중 오류가 발생했습니다.");
      setForm(buildEditForm(session, optionsQuery.data, selectedDate));
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setPhotoFile(null);
    setForm(makeEmptyForm(selectedDate, optionsQuery.data));
    setIsLoadingDetail(false);
  };

  const handleFormChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    if (!String(form.team_name || "").trim()) {
      toast.warning("팀/라인을 입력해주세요.");
      return;
    }
    if (!form.leader_emp_id) {
      toast.warning("진행자를 선택해주세요.");
      return;
    }
    if (!form.session_date) {
      toast.warning("실시 일시를 입력해주세요.");
      return;
    }
    saveMutation.mutate();
  };

  const selectedIds = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((key) => rowSelection[key])
        .map((key) => Number(key))
        .filter(Boolean),
    [rowSelection]
  );

  const handleDelete = () => {
    if (!selectedIds.length) {
      toast.warning("삭제할 TBM 실시 내역을 선택해주세요.");
      return;
    }
    if (window.confirm(`${selectedIds.length}건의 TBM 실시 내역을 삭제할까요?`)) {
      deleteMutation.mutate(selectedIds);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "session_time",
        header: "시간",
        size: 90,
        Cell: ({ cell }) => (
          <Typography sx={{ fontWeight: 800 }}>{cell.getValue() || "--:--"}</Typography>
        ),
      },
      {
        accessorKey: "team_name",
        header: "팀/라인",
        size: 180,
        Cell: ({ row }) => (
          <Stack spacing={0.3}>
            <Typography sx={{ fontWeight: 800 }}>{row.original.team_name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.leader_department || "부서 미등록"}
            </Typography>
          </Stack>
        ),
      },
      {
        accessorKey: "leader_name",
        header: "진행자",
        size: 140,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "attended_count",
        header: "참석",
        size: 180,
        Cell: ({ row }) => {
          const attended = Number(row.original.attended_count || 0);
          const total = Number(row.original.total_members || 0);
          const rate = total ? Math.round((attended / total) * 100) : 0;
          return (
            <Stack spacing={0.75}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {attended}/{total}명
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {rate}%
                </Typography>
              </Stack>
              <LinearProgress variant="determinate" value={rate} sx={{ height: 8, borderRadius: 999 }} />
            </Stack>
          );
        },
      },
      {
        accessorKey: "status",
        header: "상태",
        size: 110,
        Cell: ({ cell }) => {
          const status = getStatusMeta(cell.getValue());
          return <Chip size="small" color={status.color} label={status.label} />;
        },
      },
      {
        accessorKey: "photo_url",
        header: "현장 사진",
        size: 130,
        Cell: ({ cell }) => {
          const photoUrl = cell.getValue();
          if (!photoUrl) return <Typography color="text.secondary">-</Typography>;
          return (
            <Button
              size="small"
              href={resolvePhotoUrl(photoUrl)}
              target="_blank"
              rel="noreferrer"
              startIcon={<OpenInNewIcon />}
            >
              보기
            </Button>
          );
        },
      },
    ],
    []
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, height: "100%", overflow: "auto", bgcolor: "#f7f8fb" }}>
      <Stack spacing={2.5}>
        <Paper
          data-autopilot="tbm-header"
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <PlaylistAddCheckIcon color="primary" />
                <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 900 }}>
                  작업시작 전 안전점검회의(TBM) 관리
                </Typography>
              </Stack>
            </Box>

            <Stack data-autopilot="tbm-actions" direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField
                type="date"
                size="small"
                label="조회 일자"
                value={selectedDate}
                onChange={(event) => {
                  setSelectedDate(event.target.value);
                  setRowSelection({});
                }}
                InputLabelProps={{ shrink: true }}
              />
              <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
                <InputLabel>팀</InputLabel>
                <Select
                  label="팀"
                  value={teamFilter}
                  onChange={(event) => {
                    setTeamFilter(event.target.value);
                    setRowSelection({});
                  }}
                >
                  <MenuItem value="all">전체 팀</MenuItem>
                  {departments.map((department) => (
                    <MenuItem
                      key={department.department_id}
                      value={String(department.department_id)}
                    >
                      {department.department_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="오토파일럿">
                <IconButton
                  color="primary"
                  onClick={openAutopilot}
                  sx={{
                    width: 40,
                    height: 40,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    bgcolor: "#fff",
                    "&:hover": {
                      bgcolor: "primary.lightOpacity",
                      borderColor: "primary.main",
                    },
                  }}
                >
                  <PlayCircleOutlineIcon />
                </IconButton>
              </Tooltip>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreateDialog()}>
                {isMobile ? "기록" : "등록"}
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Grid data-autopilot="tbm-summary" container spacing={1.5}>
          <Grid item xs={6} md={2}>
            <SummaryTile icon={<PlaylistAddCheckIcon />} label="전체" value={`${summary.total}건`} />
          </Grid>
          <Grid item xs={6} md={2}>
            <SummaryTile icon={<CheckCircleIcon />} label="완료" value={`${summary.completed}건`} color="success" />
          </Grid>
          <Grid item xs={6} md={2}>
            <SummaryTile icon={<EventBusyIcon />} label="미실시" value={`${summary.missed}건`} color="error" />
          </Grid>
          <Grid item xs={6} md={2}>
            <SummaryTile icon={<CalendarMonthIcon />} label="미등록" value={`${summary.unregistered}건`} color="warning" />
          </Grid>
          <Grid item xs={6} md={2}>
            <SummaryTile icon={<GroupsIcon />} label="대상" value={`${summary.totalTargets}명`} />
          </Grid>
          <Grid item xs={6} md={2}>
            <SummaryTile
              icon={<GroupsIcon />}
              label="참석률"
              value={`${summary.rate}%`}
              color="success"
            />
          </Grid>
        </Grid>

        {sessionsQuery.isError ? (
          <Alert severity="error">TBM 데이터를 불러오는 중 오류가 발생했습니다.</Alert>
        ) : null}

        {dailyTbmStatus ? (
          <Alert
            severity={dailyTbmStatus.severity}
            sx={{ borderRadius: 2, alignItems: "center" }}
          >
            <Typography sx={{ fontWeight: 900 }}>{dailyTbmStatus.title}</Typography>
            <Typography variant="body2">{dailyTbmStatus.message}</Typography>
          </Alert>
        ) : null}

        <MonthlyTbmCalendar
          title={monthBounds.label}
          days={monthCalendarQuery.data || []}
          selectedDate={selectedDate}
          isLoading={monthCalendarQuery.isLoading}
          onMonthChange={(offset) => {
            setSelectedDate(getShiftedMonthDate(selectedDate, offset));
            setRowSelection({});
          }}
          onSelectDate={(date) => {
            setSelectedDate(date);
            setRowSelection({});
          }}
          isMobile={isMobile}
        />

        {isMobile ? (
          <Stack data-autopilot="tbm-list" spacing={1.25}>
            {sessionsQuery.isLoading ? (
              <Stack alignItems="center" sx={{ py: 6 }}>
                <CircularProgress />
              </Stack>
            ) : null}
            {visibleSessions.map((session) => (
              <MobileSessionCard
                key={session.id}
                session={session}
                onEdit={openEditDialog}
                onCreateFromTeam={openCreateDialog}
              />
            ))}
            {!sessionsQuery.isLoading && !visibleSessions.length ? (
              <EmptyState
                compact
                icon={<PlaylistAddCheckIcon />}
                title="조회된 TBM 실시 내역이 없습니다"
                description="선택한 일자에 등록된 TBM이 없습니다. 현장 실시 후 등록 버튼으로 참석자와 체크리스트를 남길 수 있습니다."
                actionLabel={isMobile ? "TBM 기록" : "TBM 등록"}
                actionIcon={<AddIcon />}
                onAction={() => openCreateDialog()}
              />
            ) : null}
          </Stack>
        ) : (
          <Paper
            data-autopilot="tbm-list"
            elevation={0}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "#fff",
            }}
          >
            <MaterialReactTable
              columns={columns}
              data={visibleSessions}
              localization={MRT_Localization_KO}
              getRowId={(row) => String(row.id)}
              enableRowSelection={(row) => !row.original.virtual}
              enableMultiRowSelection
              enableColumnActions={false}
              enableDensityToggle={false}
              enableFullScreenToggle={false}
              enableColumnFilters={false}
              enableStickyHeader
              state={{
                isLoading: sessionsQuery.isLoading,
                showProgressBars: sessionsQuery.isFetching,
                globalFilter,
                rowSelection,
              }}
              onGlobalFilterChange={setGlobalFilter}
              onRowSelectionChange={setRowSelection}
              muiTablePaperProps={{
                sx: {
                  boxShadow: "none",
                  "& *": {
                    fontFamily: `${FONT_PROPS.PRIMARY}, sans-serif !important`,
                  },
                },
              }}
              muiTableContainerProps={{
                sx: {
                  maxHeight: "calc(100vh - 390px)",
                },
              }}
              muiTableHeadCellProps={{
                sx: {
                  fontWeight: 800,
                  bgcolor: "#f1f5f9",
                },
              }}
              renderTopToolbarCustomActions={() => (
                <Stack
                  data-autopilot="tbm-table-actions"
                  direction={{ xs: "column", md: "row" }}
                  spacing={1}
                  alignItems={{ xs: "stretch", md: "center" }}
                  sx={{ p: 1, width: "100%" }}
                >
                  <TextField
                    size="small"
                    placeholder="팀/라인, 진행자 검색"
                    value={globalFilter || ""}
                    onChange={(event) => setGlobalFilter(event.target.value)}
                    sx={{ width: { xs: "100%", md: 320 } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: globalFilter ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setGlobalFilter("")}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                  <Divider orientation="vertical" flexItem />
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={!selectedIds.length || deleteMutation.isPending}
                    onClick={handleDelete}
                  >
                    삭제
                  </Button>
                </Stack>
              )}
              enableRowActions
              positionActionsColumn="last"
              renderRowActions={({ row }) => (
                row.original.virtual ? (
                  <Tooltip title="등록">
                    <IconButton color="primary" onClick={() => openCreateDialog(row.original)}>
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <Tooltip title="수정">
                    <IconButton onClick={() => openEditDialog(row.original)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                )
              )}
              renderEmptyRowsFallback={() => (
                <EmptyState
                  compact
                  icon={<PlaylistAddCheckIcon />}
                  title="조회된 TBM 실시 내역이 없습니다"
                  description="선택한 일자에 등록된 TBM이 없습니다. 등록 버튼으로 새 실시 내역을 추가할 수 있습니다."
                  actionLabel="TBM 등록"
                  actionIcon={<AddIcon />}
                  onAction={() => openCreateDialog()}
                />
              )}
            />
          </Paper>
        )}
      </Stack>

      <TbmFormDialog
        open={dialogOpen}
        mode={dialogMode}
        form={form}
        employees={employees}
        departments={departments}
        checklistTemplates={checklistTemplates}
        photoFile={photoFile}
        isMobile={isMobile}
        isSaving={saveMutation.isPending}
        isLoadingDetail={isLoadingDetail}
        onClose={closeDialog}
        onChange={handleFormChange}
        onPhotoChange={setPhotoFile}
        onSubmit={handleSubmit}
      />

      <AutoPilotGuide
        open={autopilotOpen}
        steps={autopilotSteps}
        stepIndex={autopilotStepIndex}
        onStepChange={setAutopilotStepIndex}
        onClose={() => setAutopilotOpen(false)}
      />
    </Box>
  );
}
